/**
 * CreateScheduleModal - 수업을 예약(생성)하는 모달 컴포넌트 (폼 3)
 *
 * - 수업명/Type/최대인원/시작·종료(KST)/담당강사/예약회원(다중) 입력
 * - 강사·회원 목록은 모달 open 시 센터에서 read (오타 방지 — 목록에서 선택)
 * - 수강권은 회원별로 본인 보유 수강권(getMemberTickets) 중 선택 — 수업 type(P/G) 일치만 노출
 * - `useCreateSchedule` 훅으로 Cloud Function `createSchedule` 호출
 * - 성공 시 onCreated 콜백 → 부모가 모달 close + 성공 배너
 */

import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { collection, getDocs } from "firebase/firestore";
import { getFirebaseFirestore } from "@/config/firebase";
import { getAdminMembersList, type Member } from "@/services/firestore/admin-members";
import { useCreateSchedule } from "@/hooks/useCreateSchedule";
import type { CreateScheduleParams } from "@/hooks/useCreateSchedule";
import { getMemberTickets, type MemberTicket } from "@/hooks/useTicketPurchase";

interface CreateScheduleModalProps {
  isOpen: boolean;
  /** 강사·회원 목록 read 용 (센터 소유자 uid) */
  ownerUid: string;
  centerId: number;
  /** 백엔드 lookupCenterByName 입력 */
  centerName: string;
  onClose: () => void;
  onCreated: () => void;
}

interface InstructorOption {
  /** userUid (doc id) */
  id: string;
  name: string;
  contact: string | null;
}

function getErrorMessage(error: unknown): string | null {
  if (error && typeof error === "object" && "message" in error) {
    const msg = (error as { message: unknown }).message;
    if (typeof msg === "string" && msg.length > 0) return msg;
  }
  return null;
}

/** 연락처 뒷자리만 노출 (010****5678 형식). */
function maskMobile(mobile: string | null | undefined): string {
  const digits = (mobile ?? "").replace(/[^0-9]/g, "");
  if (digits.length < 4) return mobile ?? "";
  return `****${digits.slice(-4)}`;
}

export function CreateScheduleModal({
  isOpen,
  ownerUid,
  centerId,
  centerName,
  onClose,
  onCreated,
}: CreateScheduleModalProps) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState<"P" | "G">("P");
  const [maxTraineeInput, setMaxTraineeInput] = useState("1");
  const [startOn, setStartOn] = useState("");
  const [endOn, setEndOn] = useState("");
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<Member[]>([]);
  /** member.id → 그 회원의 보유 수강권 목록 (undefined=미적재). */
  const [memberTickets, setMemberTickets] = useState<Record<string, MemberTicket[]>>({});
  /** member.id → 선택한 수강권명. */
  const [selectedTickets, setSelectedTickets] = useState<Record<string, string>>({});
  const [validationError, setValidationError] = useState<string | null>(null);

  const [instructors, setInstructors] = useState<InstructorOption[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [listError, setListError] = useState<string | null>(null);

  const createSchedule = useCreateSchedule();

  useEffect(() => {
    if (!isOpen) return;

    setTitle("");
    setType("P");
    setMaxTraineeInput("1");
    setStartOn("");
    setEndOn("");
    setSelectedStaffId("");
    setMemberSearch("");
    setSelectedMembers([]);
    setMemberTickets({});
    setSelectedTickets({});
    setValidationError(null);
    setListError(null);
    createSchedule.reset();

    let cancelled = false;
    const db = getFirebaseFirestore();
    Promise.all([
      getDocs(collection(db, "users", ownerUid, "centers", String(centerId), "instructors")),
      getAdminMembersList(ownerUid, centerId),
    ])
      .then(([instructorsSnap, memberList]) => {
        if (cancelled) return;
        setInstructors(
          instructorsSnap.docs.map((d) => {
            const data = d.data() as { name?: string; contact?: string };
            return { id: d.id, name: data.name ?? "이름 없음", contact: data.contact ?? null };
          }),
        );
        setMembers(memberList);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setListError(
            err instanceof Error ? err.message : "강사·회원 목록을 불러오지 못했습니다.",
          );
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ownerUid, centerId]);

  // 수업 type(P/G) 변경 시, 더 이상 유효하지 않은 회원별 수강권 선택을 해제한다.
  useEffect(() => {
    setSelectedTickets((prev) => {
      const next: Record<string, string> = {};
      for (const [memberId, ticketTitle] of Object.entries(prev)) {
        const stillValid = (memberTickets[memberId] ?? []).some(
          (t) => t.isActive && t.type === type && t.title === ticketTitle,
        );
        if (stillValid) next[memberId] = ticketTitle;
      }
      return next;
    });
  }, [type, memberTickets]);

  if (!isOpen) return null;

  /** 해당 회원이 현재 수업 type으로 예약 가능한 보유 수강권(활성). */
  const eligibleTicketsFor = (memberId: string): MemberTicket[] =>
    (memberTickets[memberId] ?? []).filter((t) => t.isActive && t.type === type);

  const filteredMembers = members.filter((m) => {
    if (selectedMembers.some((s) => s.id === m.id)) return false;
    const q = memberSearch.trim();
    return q === "" ? true : m.name.includes(q);
  });

  const handleAddMember = (member: Member) => {
    setSelectedMembers((prev) => (prev.some((m) => m.id === member.id) ? prev : [...prev, member]));
    setMemberSearch("");
    // 회원 보유 수강권 적재 (미적재인 경우만).
    if (memberTickets[member.id] === undefined) {
      getMemberTickets(centerId, Number(member.id))
        .then((tickets) => setMemberTickets((prev) => ({ ...prev, [member.id]: tickets })))
        .catch(() => setMemberTickets((prev) => ({ ...prev, [member.id]: [] })));
    }
  };

  const handleRemoveMember = (memberId: string) => {
    setSelectedMembers((prev) => prev.filter((m) => m.id !== memberId));
    setSelectedTickets((prev) => {
      const next = { ...prev };
      delete next[memberId];
      return next;
    });
  };

  const handleSelectTicket = (memberId: string, ticketTitle: string) => {
    setSelectedTickets((prev) => {
      if (ticketTitle === "") {
        const next = { ...prev };
        delete next[memberId];
        return next;
      }
      return { ...prev, [memberId]: ticketTitle };
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setValidationError("수업명을 입력해주세요.");
      return;
    }
    const maxTrainee = Number(maxTraineeInput);
    if (!Number.isInteger(maxTrainee) || maxTrainee < 1) {
      setValidationError("최대 인원은 1 이상의 정수로 입력해주세요.");
      return;
    }
    if (!startOn || !endOn) {
      setValidationError("수업 시작·종료 시각을 입력해주세요.");
      return;
    }
    if (endOn <= startOn) {
      setValidationError("수업 종료 시각은 시작 시각보다 뒤여야 합니다.");
      return;
    }
    const staff = instructors.find((i) => i.id === selectedStaffId);
    if (!staff) {
      setValidationError("담당 강사를 선택해주세요.");
      return;
    }
    if (selectedMembers.length === 0) {
      setValidationError("예약 회원을 1명 이상 선택해주세요.");
      return;
    }
    const missingTicket = selectedMembers.some((m) => !selectedTickets[m.id]);
    if (missingTicket) {
      setValidationError("모든 예약 회원의 수강권을 선택해주세요.");
      return;
    }

    setValidationError(null);

    const params: CreateScheduleParams = {
      centerName,
      title: trimmedTitle,
      type,
      maxTrainee,
      startOn,
      endOn,
      staffName: staff.name,
      ...(staff.contact ? { staffMobile: staff.contact } : {}),
      members: selectedMembers.map((m) => ({
        name: m.name,
        mobile: m.contact ?? "",
        ticketTitle: selectedTickets[m.id],
      })),
    };

    createSchedule.mutate(params, {
      onSuccess: () => {
        onCreated();
      },
    });
  };

  const serverErrorMessage = createSchedule.isError ? getErrorMessage(createSchedule.error) : null;
  const isSubmitDisabled =
    !title.trim() ||
    !selectedStaffId ||
    selectedMembers.length === 0 ||
    selectedMembers.some((m) => !selectedTickets[m.id]) ||
    !startOn ||
    !endOn ||
    createSchedule.isPending;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">수업 예약</h2>
        <p className="text-sm text-gray-500 mb-4">센터: {centerName}</p>

        {listError && <p className="text-sm text-red-600 mb-4">{listError}</p>}

        <form onSubmit={handleSubmit}>
          {/* 수업명 */}
          <div className="mb-4">
            <label htmlFor="scheduleTitle" className="block text-sm font-medium text-gray-700 mb-1">
              수업명 <span className="text-red-500">*</span>
            </label>
            <input
              id="scheduleTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: 1:1 필라테스"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Type + 최대 인원 */}
          <div className="mb-4 flex gap-3">
            <div className="flex-1">
              <label
                htmlFor="scheduleType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Type <span className="text-red-500">*</span>
              </label>
              <select
                id="scheduleType"
                value={type}
                onChange={(e) => setType(e.target.value as "P" | "G")}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="P">P (Private)</option>
                <option value="G">G (Group)</option>
              </select>
            </div>
            <div className="flex-1">
              <label
                htmlFor="scheduleMaxTrainee"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                최대 인원 <span className="text-red-500">*</span>
              </label>
              <input
                id="scheduleMaxTrainee"
                type="number"
                step={1}
                min={1}
                value={maxTraineeInput}
                onChange={(e) => setMaxTraineeInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* 시작 / 종료 (KST) */}
          <div className="mb-4 flex gap-3">
            <div className="flex-1">
              <label
                htmlFor="scheduleStartOn"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                시작 (KST) <span className="text-red-500">*</span>
              </label>
              <input
                id="scheduleStartOn"
                type="datetime-local"
                value={startOn}
                onChange={(e) => setStartOn(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div className="flex-1">
              <label
                htmlFor="scheduleEndOn"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                종료 (KST) <span className="text-red-500">*</span>
              </label>
              <input
                id="scheduleEndOn"
                type="datetime-local"
                value={endOn}
                onChange={(e) => setEndOn(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* 담당 강사 */}
          <div className="mb-4">
            <label htmlFor="scheduleStaff" className="block text-sm font-medium text-gray-700 mb-1">
              담당 강사 <span className="text-red-500">*</span>
            </label>
            <select
              id="scheduleStaff"
              value={selectedStaffId}
              onChange={(e) => setSelectedStaffId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white"
              required
            >
              <option value="">강사 선택</option>
              {instructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                  {i.contact ? ` (${maskMobile(i.contact)})` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* 예약 회원 (다중) */}
          <div className="mb-4">
            <label
              htmlFor="scheduleMemberSearch"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              예약 회원 <span className="text-red-500">*</span>{" "}
              <span className="text-xs text-gray-400">— 한 명 이상</span>
            </label>
            <input
              id="scheduleMemberSearch"
              type="text"
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="회원 검색..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            {memberSearch.trim() !== "" && filteredMembers.length > 0 && (
              <ul className="border border-gray-200 rounded-md mt-1 max-h-40 overflow-y-auto">
                {filteredMembers.slice(0, 20).map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => handleAddMember(m)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    >
                      {m.name}
                      {m.contact ? ` (${maskMobile(m.contact)})` : ""}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {selectedMembers.length > 0 && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-gray-400">회원별로 본인 보유 수강권을 선택하세요.</p>
                {selectedMembers.map((m) => {
                  const loaded = memberTickets[m.id] !== undefined;
                  const eligible = eligibleTicketsFor(m.id);
                  return (
                    <div
                      key={m.id}
                      className="flex items-center gap-2 p-2 border border-gray-200 rounded-md"
                    >
                      <span className="text-sm text-gray-800 whitespace-nowrap">
                        {m.name}
                        {m.contact ? ` (${maskMobile(m.contact)})` : ""}
                      </span>
                      <div className="flex-1">
                        {!loaded ? (
                          <span className="text-xs text-gray-400">수강권 불러오는 중...</span>
                        ) : eligible.length === 0 ? (
                          <span className="text-xs text-red-500">보유 수강권 없음</span>
                        ) : (
                          <select
                            aria-label={`${m.name} 수강권`}
                            value={selectedTickets[m.id] ?? ""}
                            onChange={(e) => handleSelectTicket(m.id, e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">수강권 선택</option>
                            {eligible.map((t, idx) => (
                              <option key={`${t.title}-${idx}`} value={t.title}>
                                {t.title} (잔여 {t.remainingCoupon}/{t.maxCoupon})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <button
                        type="button"
                        aria-label={`${m.name} 제거`}
                        onClick={() => handleRemoveMember(m.id)}
                        className="text-blue-500 hover:text-blue-800 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 검증 / 서버 에러 */}
          {validationError && <p className="text-sm text-red-600 mb-4">{validationError}</p>}
          {createSchedule.isError && !validationError && (
            <p className="text-sm text-red-600 mb-4">
              {serverErrorMessage ?? "수업 예약에 실패했습니다. 다시 시도해주세요."}
            </p>
          )}

          {/* 버튼 */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createSchedule.isPending ? "예약 중..." : "예약"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
